import * as React from 'react'

import { usPhoneDigits } from '#/shared/lib/format'
import { cn } from '#/shared/lib/utils'
import { ClearIcon } from '#/shared/ui/icons'
import { Label } from '#/shared/ui/label'
import type { LabelSize } from '#/shared/ui/label'

interface InputProps extends React.ComponentProps<'input'> {
  label?: string
  labelSize?: LabelSize
  error?: boolean
  success?: boolean
  clearable?: boolean
  onClear?: () => void
  startAdornment?: React.ReactNode
  endAdornment?: React.ReactNode
  variant?: 'default' | 'filled'
  inputSize?: 'default' | 'md' | 'sm'
  mask?: string
  numberFormat?: boolean
  phoneFormat?: boolean
  cardFormat?: boolean
}

function formatThousands(raw: string): string {
  if (raw === '') return ''
  const normalized = raw.replace(/,/g, '')
  const negative = normalized.startsWith('-')
  const body = negative ? normalized.slice(1) : normalized
  const hasDecimal = body.includes('.')
  const [intPart = '', decPart = ''] = body.split('.')
  const cleanedInt = intPart.replace(/\D/g, '')
  const withSeparators = cleanedInt
    ? cleanedInt.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    : ''
  const sign = negative ? '-' : ''
  if (!hasDecimal) return `${sign}${withSeparators}`
  const cleanedDec = decPart.replace(/\D/g, '')
  return `${sign}${withSeparators || '0'}.${cleanedDec}`
}

function stripThousands(s: string): string {
  return String(s).replace(/,/g, '')
}

function formatPhone(raw: string): string {
  const digits = usPhoneDigits(raw)
  if (digits.length === 0) return ''
  if (digits.length < 3) return `(${digits}`
  if (digits.length === 3) return `(${digits}) `
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

function stripPhone(s: string): string {
  return usPhoneDigits(s)
}

interface FormatSpec {
  format: (raw: string) => string
  strip: (formatted: string) => string
}

function useFormattedInput(
  spec: FormatSpec | null,
  value: string | number | readonly string[] | undefined,
  defaultValue: string | number | readonly string[] | undefined,
  onChange: React.ChangeEventHandler<HTMLInputElement> | undefined,
) {
  const enabled = spec !== null
  const [display, setDisplay] = React.useState<string>(() => {
    if (!spec) return ''
    const initial = value ?? defaultValue
    if (initial === undefined || initial === '') return ''
    return spec.format(String(initial))
  })

  React.useEffect(() => {
    if (!spec || value === undefined) return
    setDisplay(value === '' ? '' : spec.format(String(value)))
  }, [spec, value])

  const handler = React.useMemo<
    React.ChangeEventHandler<HTMLInputElement> | undefined
  >(() => {
    if (!spec) return onChange
    return (e) => {
      const nextRaw = e.target.value
      const prevDigits = spec.strip(display)
      const nextDigits = spec.strip(nextRaw)
      const onlyFormatRemoved =
        nextRaw.length < display.length &&
        nextDigits.length === prevDigits.length
      const effectiveDigits = onlyFormatRemoved
        ? nextDigits.slice(0, -1)
        : nextDigits
      const formatted = spec.format(effectiveDigits)
      setDisplay(formatted)
      if (!onChange) return
      const raw = spec.strip(formatted)
      const proxyTarget = {
        value: raw,
        name: e.target.name,
        type: e.target.type,
      } as unknown as HTMLInputElement
      onChange({
        ...e,
        target: proxyTarget,
        currentTarget: proxyTarget,
      })
    }
  }, [spec, onChange, display])

  return {
    effectiveValue: enabled ? display : value,
    effectiveDefault: enabled ? undefined : defaultValue,
    effectiveOnChange: handler,
  }
}

function formatCardNumber(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 19)
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ')
}

function stripCardNumber(s: string): string {
  return String(s).replace(/\D/g, '')
}

const PHONE_SPEC: FormatSpec = { format: formatPhone, strip: stripPhone }
const NUMBER_SPEC: FormatSpec = {
  format: formatThousands,
  strip: stripThousands,
}
const CARD_SPEC: FormatSpec = {
  format: formatCardNumber,
  strip: stripCardNumber,
}

const sizeClasses = {
  default: { container: 'h-12 gap-2 px-2.5 rounded-sm', input: 'text-base' },
  md: { container: 'h-11 gap-2 px-2.5 rounded-sm', input: 'text-sm' },
  sm: { container: 'h-8 gap-1 px-1.25 rounded-sm', input: 'text-sm' },
} as const

type InputSize = 'default' | 'md' | 'sm'

function getContainerClasses(
  inputSize: InputSize,
  error: boolean,
  success: boolean,
  variant: 'default' | 'filled',
  disabled: boolean,
  className?: string,
) {
  return cn(
    'group/input relative flex w-full items-center border transition-[border-color] duration-200',
    sizeClasses[inputSize].container,
    error
      ? 'border-red-500 focus-within:focus-ring-error'
      : success
        ? 'border-green-500 focus-within:focus-ring'
        : 'border-gray-200 focus-within:focus-ring',
    variant === 'filled' ? 'bg-gray-50' : 'bg-white',
    disabled && 'cursor-not-allowed opacity-50',
    className,
  )
}

export function ClearButton({
  onClick,
  hidden = false,
}: {
  onClick: () => void
  hidden?: boolean
}) {
  return (
    <button
      type="button"
      tabIndex={-1}
      aria-hidden={hidden}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      data-slot="clear-button"
      className={cn(
        'group flex size-4.5 min-w-4.5 shrink-0 cursor-pointer items-center justify-center rounded-full text-gray-500 transition-colors hover:text-gray-900',
        hidden && 'pointer-events-none invisible',
      )}
    >
      <ClearIcon className="block size-4.5 transition-transform duration-200 group-hover:rotate-90" />
    </button>
  )
}

function useClearable(
  ref: React.RefObject<HTMLInputElement | null>,
  value: string | number | readonly string[] | undefined,
  defaultValue: string | number | readonly string[] | undefined,
  onChange: React.ChangeEventHandler<HTMLInputElement> | undefined,
  onClear: (() => void) | undefined,
) {
  const [hasValue, setHasValue] = React.useState(
    () => !!(value ?? defaultValue),
  )

  React.useEffect(() => {
    if (value !== undefined) {
      setHasValue(!!value)
      return
    }
    const input = ref.current
    if (input) setHasValue(!!input.value)
  }, [value])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasValue(!!e.target.value)
    onChange?.(e)
  }

  const handleClear = () => {
    const input = ref.current
    if (!input) return
    const setter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      'value',
    )?.set
    setter?.call(input, '')
    input.dispatchEvent(new Event('input', { bubbles: true }))
    setHasValue(false)
    input.focus()
    onClear?.()
  }

  return { hasValue, handleChange, handleClear }
}

function useCombinedRefs<T>(...refs: Array<React.Ref<T> | undefined>) {
  return React.useCallback((node: T | null) => {
    for (const ref of refs) {
      if (!ref) continue
      if (typeof ref === 'function') ref(node)
      else (ref as React.MutableRefObject<T | null>).current = node
    }
  }, refs)
}

function Adornment({ children }: { children: React.ReactNode }) {
  return (
    <span className="flex shrink-0 items-center text-gray-300">{children}</span>
  )
}

type InputCoreProps = {
  inputId: string
  inputSize: InputSize
  mask?: string
  value?: string | number | readonly string[]
  defaultValue?: string | number | readonly string[]
  handleChange: React.ChangeEventHandler<HTMLInputElement>
  combinedRef: React.Ref<HTMLInputElement>
  type?: string
} & Omit<
  React.ComponentProps<'input'>,
  'size' | 'value' | 'defaultValue' | 'onChange' | 'ref' | 'type'
>

function InputCore({
  inputId,
  inputSize,
  mask,
  value,
  defaultValue,
  handleChange,
  combinedRef,
  type,
  ...props
}: InputCoreProps) {
  const hasValue =
    (value !== undefined && value !== '') ||
    (defaultValue !== undefined && defaultValue !== '')
  return (
    <div className="relative flex h-full min-w-0 flex-1 items-center">
      {mask && (
        <span
          aria-hidden
          className={cn(
            'pointer-events-none absolute mt-0.5 inset-y-0 left-0 flex items-center select-none',
            hasValue ? 'text-gray-900' : 'text-gray-300',
            sizeClasses[inputSize].input,
          )}
        >
          {mask}
        </span>
      )}
      <input
        id={inputId}
        type={type}
        value={value}
        defaultValue={defaultValue}
        onChange={handleChange}
        className={cn(
          // input-mobile-safe holds a 16px floor on phones so focusing the field
          // never triggers iOS auto-zoom (desktop keeps the size class below).
          'input-mobile-safe h-full w-full min-w-0 flex-1 bg-transparent placeholder:text-gray-300 focus-visible:outline-none disabled:cursor-not-allowed',
          mask ? 'pl-2.5' : '',
          sizeClasses[inputSize].input,
        )}
        ref={combinedRef}
        {...props}
      />
    </div>
  )
}

function LabeledInput({
  label,
  labelSize,
  inputId,
  children,
}: {
  label: string
  labelSize: LabelSize
  inputId: string
  children: React.ReactNode
}) {
  return (
    <div
      className={cn('flex flex-col', labelSize === 'sm' ? 'gap-0.5' : 'gap-1')}
    >
      <Label htmlFor={inputId} size={labelSize}>
        {label}
      </Label>
      {children}
    </div>
  )
}

type InputContainerProps = InputCoreProps & {
  error: boolean
  success: boolean
  variant: 'default' | 'filled'
  disabled: boolean
  className?: string
  startAdornment?: React.ReactNode
  endAdornment?: React.ReactNode
  reserveClear: boolean
  showClear: boolean
  handleClear: () => void
}

function InputContainer({
  error,
  success,
  variant,
  disabled,
  className,
  startAdornment,
  endAdornment,
  showClear,
  handleClear,
  reserveClear: _reserveClear,
  ...coreProps
}: InputContainerProps) {
  void _reserveClear
  return (
    <div
      data-slot="input-container"
      className={getContainerClasses(
        coreProps.inputSize,
        error,
        success,
        variant,
        disabled,
        className,
      )}
    >
      {startAdornment && <Adornment>{startAdornment}</Adornment>}
      {/* `disabled` is destructured out above for container styling — pass
       * it back explicitly so the actual <input> also becomes disabled and
       * the browser stops accepting keystrokes. */}
      <InputCore {...coreProps} disabled={disabled} />
      {endAdornment && <Adornment>{endAdornment}</Adornment>}
      {showClear && (
        <div
          className={cn(
            'pointer-events-none absolute top-1/2 -translate-y-1/2 opacity-0 transition-opacity duration-150',
            'group-hover/input:pointer-events-auto group-hover/input:opacity-100',
            'group-focus-within/input:pointer-events-auto group-focus-within/input:opacity-100',
            coreProps.inputSize === 'sm' ? 'right-1.25' : 'right-2.5',
          )}
        >
          <ClearButton onClick={handleClear} />
        </div>
      )}
    </div>
  )
}

function pickFormatSpec(
  phoneFormat: boolean,
  numberFormat: boolean,
  cardFormat: boolean,
): FormatSpec | null {
  if (phoneFormat) return PHONE_SPEC
  if (numberFormat) return NUMBER_SPEC
  if (cardFormat) return CARD_SPEC
  return null
}

function useInputSetup(props: InputProps, ref: React.Ref<HTMLInputElement>) {
  const {
    type,
    numberFormat = false,
    phoneFormat = false,
    cardFormat = false,
    onChange,
    value,
    defaultValue,
    clearable = false,
    onClear,
  } = props
  const innerRef = React.useRef<HTMLInputElement>(null)
  const combinedRef = useCombinedRefs(ref, innerRef)
  const disabled = !!props.disabled

  const formatSpec = pickFormatSpec(phoneFormat, numberFormat, cardFormat)
  const effectiveType = phoneFormat ? 'tel' : type
  const effectiveInputMode = phoneFormat ? 'tel' : props.inputMode

  const { effectiveValue, effectiveDefault, effectiveOnChange } =
    useFormattedInput(formatSpec, value, defaultValue, onChange)

  const { hasValue, handleChange, handleClear } = useClearable(
    innerRef,
    effectiveValue,
    effectiveDefault,
    effectiveOnChange,
    onClear,
  )
  const reserveClear = clearable && !disabled
  const showClear = reserveClear && hasValue

  return {
    disabled,
    combinedRef,
    effectiveType,
    effectiveInputMode,
    effectiveValue,
    effectiveDefault,
    handleChange,
    handleClear,
    reserveClear,
    showClear,
  }
}

function InputInner(
  {
    className,
    label,
    labelSize = 'default',
    id,
    error = false,
    success = false,
    startAdornment,
    endAdornment,
    variant = 'default',
    inputSize = 'default',
    mask,
    ...rest
  }: InputProps,
  ref: React.Ref<HTMLInputElement>,
) {
  const inputId = id ?? React.useId()
  const restWithRef = rest as InputProps & {
    ref?: React.Ref<HTMLInputElement>
  }
  const externalRef = restWithRef.ref
  const {
    ref: _omitRef,
    onChange: _omitOnChange,
    value: _omitValue,
    defaultValue: _omitDefaultValue,
    clearable: _omitClearable,
    onClear: _omitOnClear,
    numberFormat: _omitNumberFormat,
    phoneFormat: _omitPhoneFormat,
    cardFormat: _omitCardFormat,
    labelSize: _omitLabelSize,
    ...safeRest
  } = restWithRef
  void _omitRef
  void _omitOnChange
  void _omitValue
  void _omitDefaultValue
  void _omitClearable
  void _omitOnClear
  void _omitNumberFormat
  void _omitPhoneFormat
  void _omitCardFormat
  void _omitLabelSize
  const mergedForwardRef = useCombinedRefs(ref, externalRef)
  const setup = useInputSetup({ ...rest, className }, mergedForwardRef)

  const containerProps = {
    inputId,
    type: setup.effectiveType,
    value: setup.effectiveValue,
    defaultValue: setup.effectiveDefault,
    inputSize,
    error,
    success,
    variant,
    disabled: setup.disabled,
    className,
    startAdornment,
    endAdornment,
    mask,
    reserveClear: setup.reserveClear,
    showClear: setup.showClear,
    handleChange: setup.handleChange,
    handleClear: setup.handleClear,
    combinedRef: setup.combinedRef,
    ...safeRest,
    inputMode: setup.effectiveInputMode,
  }

  const container = <InputContainer {...containerProps} />
  if (!label) return container
  return (
    <LabeledInput label={label} labelSize={labelSize} inputId={inputId}>
      {container}
    </LabeledInput>
  )
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(InputInner)
Input.displayName = 'Input'

export { Input }
export type { InputProps }
